import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useConsignorId } from '../hooks/useConsignorId';
import { ConsignmentOrder } from '../types/consignment.types';

// ─── Local types ──────────────────────────────────────────────────────────────

interface AuctionLotItem {
  id: string;
  lotNumber?: string;
  sku?: string;
  title?: string;
  description?: string;
  images?: string[];
  currentBid?: number;   // CENTS (like every other lot price); importer seeds it = startingBid
  startingBid?: number;  // CENTS
  bidCount?: number;     // the reliable "has a real bid" signal (currentBid is seeded, so it isn't)
  status?: string;
  consignmentOrderId?: string;
  [key: string]: any;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CLASSES: Record<string, string> = {
  draft:             'bg-gray-100 text-gray-700',
  pending_signature: 'bg-yellow-100 text-yellow-800',
  active:            'bg-green-100 text-green-800',
  settled:           'bg-blue-100 text-blue-800',
  cancelled:         'bg-red-100 text-red-700',
  receiving:         'bg-orange-100 text-orange-800',
  processing:        'bg-yellow-100 text-yellow-800',
  cataloged:         'bg-sky-100 text-sky-800',
  live:              'bg-green-100 text-green-800',
  sold:              'bg-blue-100 text-blue-800',
  unsold:            'bg-orange-100 text-orange-800',
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_CLASSES[status] ?? 'bg-gray-100 text-gray-700'}`}>
    {status.replace(/_/g, ' ')}
  </span>
);

// ─── Component ────────────────────────────────────────────────────────────────

const ConsignorCOView: React.FC<{ consignorIdOverride?: string; basePath?: string }> = ({
  consignorIdOverride,
  basePath = '/consignor',
}) => {
  const { coId } = useParams<{ coId: string }>();
  const navigate = useNavigate();
  const { consignorId, loading: idLoading } = useConsignorId(consignorIdOverride);

  const [co, setCo] = useState<(Partial<ConsignmentOrder> & { id: string }) | null>(null);
  const [auctionTitle, setAuctionTitle] = useState<string>('');
  const [items, setItems] = useState<AuctionLotItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!consignorId || !coId) return;
    loadCO(consignorId, coId);
  }, [consignorId, coId]);

  const loadCO = async (cid: string, id: string) => {
    try {
      // Load CO document
      const snap = await getDoc(doc(db, 'consignmentOrders', id));
      if (!snap.exists()) {
        toast.error('Consignment order not found.');
        navigate(`${basePath}/dashboard`);
        return;
      }

      const data = snap.data() as ConsignmentOrder;

      // Security check
      if (data.consignorId !== cid) {
        toast.error('You do not have access to this order.');
        navigate(`${basePath}/dashboard`);
        return;
      }

      setCo({ ...data, id: snap.id });

      // Load auction title and lots if we have an auctionId
      if (data.auctionId) {
        const auctionSnap = await getDoc(doc(db, 'auctions', data.auctionId));
        if (auctionSnap.exists()) {
          const auctionData = auctionSnap.data();
          setAuctionTitle(auctionData.title || 'Auction');
          
          // Get lots array from auction document and filter by CO
          const allLots = auctionData.lots || [];
          const coLots = allLots.filter((lot: any) => lot.consignmentOrderId === id);
          
          // Map to proper format
          const mappedLots: AuctionLotItem[] = coLots.map((lot: any, index: number) => ({
            id: `lot-${index}`,
            ...lot,
          }));
          
          // Sort by lot number
          mappedLots.sort((a, b) => {
            const lotA = parseInt(a.lotNumber || '0', 10);
            const lotB = parseInt(b.lotNumber || '0', 10);
            return lotA - lotB;
          });
          
          setItems(mappedLots);
        }
      }
    } catch (err) {
      console.error('Error loading CO:', err);
      toast.error('Failed to load consignment order.');
    } finally {
      setLoading(false);
    }
  };

  if (idLoading || loading) {
    return (
      <div className="max-w-5xl mx-auto card py-20 text-center text-gray-500">
        <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-3" />
        <p>Loading order…</p>
      </div>
    );
  }

  if (!co) return null;

  const totals = co.totals;
  const soldItems = items.filter(it => it.status === 'sold');
  const unsoldItems = items.filter(it => it.status === 'unsold');
  const liveItems = items.filter(it => it.status === 'active' || it.status === 'live');

  return (
    <div className="max-w-5xl mx-auto space-y-6">

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500">
        <Link to={`${basePath}/dashboard`} className="hover:text-primary-600 transition-colors">My Portal</Link>
        <span>/</span>
        {co.contractId && (
          <>
            <Link to={`${basePath}/contracts/${co.contractId}`} className="hover:text-primary-600 transition-colors">
              Contract {co.contractId}
            </Link>
            <span>/</span>
          </>
        )}
        <span className="text-gray-900 font-medium">{co.id}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-mono">{co.id}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {co.commissionRate != null ? `${co.commissionRate}% commission` : '—% commission'}
            {co.createdAt ? ` · Created ${format((co.createdAt as any)?.toDate?.() ?? co.createdAt, 'MMM d, yyyy')}` : ''}
          </p>
          {auctionTitle && (
            <p className="text-sm text-gray-600 mt-1 font-medium">
              📍 {auctionTitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {co.status && <StatusBadge status={co.status} />}
          {co.auctionId && (
            <Link
              to={`/auctions/${co.auctionId}`}
              className="text-sm text-primary-600 hover:underline font-medium"
            >
              View Auction →
            </Link>
          )}
        </div>
      </div>

      {/* ── Stats Summary ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Items',  value: items.length, color: 'text-gray-900' },
          { label: 'Live/Active',  value: liveItems.length, color: 'text-green-600' },
          { label: 'Sold',         value: soldItems.length, color: 'text-blue-600' },
          { label: 'Unsold',       value: unsoldItems.length, color: 'text-orange-600' },
        ].map(stat => (
          <div key={stat.label} className="card py-4 px-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{stat.label}</p>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* ── Items Gallery ──────────────────────────────────────────────────── */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Your Auction Items ({items.length})</h2>
        </div>

        {items.length === 0 ? (
          <p className="text-center py-10 text-gray-400 text-sm italic">
            No items found in the auction for this consignment order.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(item => (
              <div key={item.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                {/* Image */}
                <div className="aspect-square bg-gray-100 relative">
                  {item.images && item.images.length > 0 ? (
                    <img
                      src={item.images.find(img => !img.includes('_00.jpg')) || item.images[0]}
                      alt={item.title || 'Item'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://placehold.co/400x400/e5e7eb/9ca3af?text=No+Image';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  {/* Status badge overlay */}
                  {item.status && (
                    <div className="absolute top-2 right-2">
                      <StatusBadge status={item.status} />
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-mono">
                        Lot #{item.lotNumber || '—'} · SKU {item.sku || '—'}
                      </p>
                      <p className="text-sm font-semibold text-gray-900 line-clamp-2 mt-0.5">
                        {item.title || 'Untitled Item'}
                      </p>
                    </div>
                  </div>

                  {item.description && (
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {item.description}
                    </p>
                  )}

                  {/* Price. Values are stored in CENTS (÷100 to display, matching SalePage /
                      AuctionDetails). "Current bid" ONLY when the lot has real bids (bidCount) —
                      the importer seeds currentBid = startingBid, so a non-null currentBid is
                      NOT proof of a bid; before the fix this rendered the seeded starting price
                      as an un-divided "Current bid" (e.g. 300¢ shown as "$300.00"). */}
                  <div className="pt-2 border-t border-gray-100">
                    {(() => {
                      const hasBid = (item.bidCount ?? 0) > 0;
                      const isSold = item.status === 'sold';
                      const cents = hasBid
                        ? (item.currentBid ?? item.startingBid)
                        : (item.startingBid ?? item.currentBid);
                      if (cents == null) {
                        return (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Starting bid:</span>
                            <span className="text-sm text-gray-400">Not set</span>
                          </div>
                        );
                      }
                      const label = isSold ? 'Sold for:' : hasBid ? 'Current bid:' : 'Starting bid:';
                      const color = isSold ? 'text-blue-600' : hasBid ? 'text-green-600' : 'text-gray-700';
                      return (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">{label}</span>
                          <span className={`text-sm font-semibold ${color}`}>
                            ${(cents / 100).toFixed(2)}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Financial Breakdown (if auction ended) ──────────────────────── */}
      {totals && totals.itemsSold > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card py-4 px-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Items Sold</p>
              <p className="text-2xl font-bold text-green-600">{totals.itemsSold}</p>
            </div>
            <div className="card py-4 px-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Gross Sales</p>
              <p className="text-2xl font-bold text-gray-900">${(totals.grossSales ?? 0).toFixed(2)}</p>
            </div>
            <div className="card py-4 px-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Commission</p>
              <p className="text-2xl font-bold text-red-600">-${(totals.commission ?? 0).toFixed(2)}</p>
            </div>
            <div className="card py-4 px-5 bg-green-50 border border-green-200">
              <p className="text-xs text-green-700 uppercase tracking-wide mb-1 font-semibold">Net to You</p>
              <p className="text-2xl font-bold text-green-700">${(totals.netToConsignor ?? 0).toFixed(2)}</p>
            </div>
          </div>

          <div className="card max-w-sm ml-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Financial Breakdown</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Gross Sales</span>
                <span className="font-semibold text-gray-900">${(totals.grossSales ?? 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Commission ({co.commissionRate}%)</span>
                <span className="text-red-600">−${(totals.commission ?? 0).toFixed(2)}</span>
              </div>
              {totals.fees > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Fees (marketing, labor)</span>
                  <span className="text-red-600">−${totals.fees.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-3 border-t-2 border-gray-200 mt-2">
                <span className="font-bold text-gray-900 text-base">Net to You</span>
                <span className="font-bold text-green-600 text-xl">${(totals.netToConsignor ?? 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Back */}
      <div className="flex gap-3">
        <Link to={`${basePath}/dashboard`} className="btn-secondary">← Back to Portal</Link>
        {co.contractId && (
          <Link to={`${basePath}/contracts/${co.contractId}`} className="btn-secondary">
            View Contract
          </Link>
        )}
      </div>
    </div>
  );
};

export default ConsignorCOView;